// adminSections.ts
import { isCarrierBridgeEnabled } from "@shared/config/featureFlags";

export interface AdminSection {
    label: string;
    route?: string;
    id?: string;
    children?: AdminSection[];
    /** Required permission(s) to see this section. If array, ANY permission grants access. */
    requiredPermission?: string | string[];
    /** If true, clicking the parent item navigates to its route and children are always visible (no dropdown) */
    clickableParent?: boolean;
    /** Optional icon identifier rendered beside the label in AdminCenterMenu */
    icon?: string;
  }
  
export const adminSections: AdminSection[] = [
  { label: 'Resource Manager', 
    id: 'resourceManager',
    requiredPermission: 'Tenant.list',
    children: [
      {
        label: 'Data Center',
        route: '/kontrol/admin-center/datacenter-inventory' 
      },
      { label: 'Hardware', 
        id: 'hardware',
        route: '/kontrol/admin-center/hardware',
        clickableParent: true,
        children: [
          {
            label: "Port",
            route:"/kontrol/admin-center/hardware/port"
          },
        ]
      },
      {
        label: 'Data Center Connections',
        route: '/kontrol/admin-center/datacenter-connections' 
      },
      { label: 'Internet',
        route: '/kontrol/admin-center/dci-internet'
      },
      { label: 'Cloud connectivity',
        route: '/kontrol/admin-center/cloud'
      },
      { label: 'Third party Ports',
        route: '/kontrol/admin-center/third-party'
      },
      { label: 'Fiber coverage',
        route: '/kontrol/admin-center/lastMile'
      },
      { label: 'IP block',
        route: '/kontrol/admin-center/ip-block'
      },
      { label: 'Public subnet stock',
        route: '/kontrol/admin-center/public-subnet'
      },
      { label: 'Network Insight',
        route: '/kontrol/admin-center/ke-topology'
      },
    ]
  },
  { label: 'Tenant', 
    id: 'tenants',
    requiredPermission: 'Tenant.list',
    children: [
      {
        label: "Partners",
        route:"/kontrol/admin-center/partners"
      },
      {
        label: "Customers",
        route:"/kontrol/admin-center/customers"
      }
    ]
  },
  { label: 'Kontrol Edge', 
    id: 'kontrolEdge',
    requiredPermission: 'Tenant.list',
    children: [
      {
        label: "Port",
        route:"/kontrol/admin-center/port"
      },
      {
        label: "Internet",
        route:"/kontrol/admin-center/internet"
      },
      {
        label: "Cloud Connectivity",
        route:"/kontrol/admin-center/cloud"
      },
      {
        label: "Datacenter Interconnect",
        route:"/kontrol/admin-center/data-center"
      },
      {
        label: "FWaaS",
        route:"/kontrol/admin-center/fwaas"
      },
      // Carrier Bridge is BETA until its name and SKU are final (CTX-955):
      // hidden in production with its routes, via isCarrierBridgeEnabled().
      ...(isCarrierBridgeEnabled()
        ? [
            {
              label: "Carrier Bridge",
              route: "/kontrol/admin-center/carrier-bridge"
            },
            {
              label: "Carriers",
              route: "/kontrol/admin-center/carriers"
            }
          ]
        : [])
    ]
  },
  { label: 'FortiFlex',
    id: 'fortiflex',
    requiredPermission: 'Tenant.list',
    children: [
      {
        label: 'FortiFlex Report',
        route: '/kontrol/admin-center/fortiflex'
      }
    ]
  },
  { label: 'Sales Tools', 
    id: 'salesTools',
    children: [
      {
        label: "Presales Designer",
        route:"/kontrol/admin-center/presales-designer",
        requiredPermission: 'Presales.Designer'
      },
      {
        label: "AI Quotation Assistant",
        route:"/kontrol/admin-center/ai-quotation",
        requiredPermission: 'Presales.Chat'
      }
    ]
  },
  // Subscriptions management (CTX-26). Own top-level section rather than a
  // child of "Kontrol Edge": it is cross-service — one table covering ports,
  // internet, DCI, cloud connectivity and the rest — so it is not a sibling of
  // the per-service admin views.
  //
  // No `requiredPermission` on purpose. The other sections gate on the
  // delegable `Tenant.list`, but this one has to agree with its API, which
  // answers 403 to anything that is not tenant type GLOBAL_ADMIN. The rule
  // lives in `getSubscriptionsAllowedTenantTypes()` and is applied by
  // `filterSubscriptionsAdminSection` so the menu and the route guard cannot
  // drift apart.
  {
    label: 'Subscriptions',
    id: 'subscriptions',
    route: '/kontrol/admin-center/subscriptions'
  },
  // Extended PoP Carrier Access (CTX-830): declare the 84-, change its legs
  // and capture each carrier's medium. Like Subscriptions, no
  // `requiredPermission`: its API is Global Admin only, which
  // `filterExtenderPopCarrierAccessAdminSection` enforces on the menu.
  {
    label: 'CPE Backhaul',
    id: 'extenderPopCarrierAccess',
    route: '/kontrol/admin-center/extended-pop-carrier-access'
  },
  // Scheduled maintenance windows (CTX-1294): Networking picks the affected
  // customers and services, and each customer sees the window on its Overview.
  // Like Subscriptions, no `requiredPermission`: its API is Global Admin only,
  // which `filterMaintenanceWindowsAdminSection` enforces on the menu.
  {
    label: 'Maintenance windows',
    id: 'maintenanceWindows',
    route: '/kontrol/admin-center/maintenance-windows'
  },
];

/**
 * CTX Reflector v1: only `reflector/admin-center/management/licensing` is registered in `ctxReflector/routes`.
 * Add entries here if new admin-center routes are added.
 */
export const reflectorV1AdminSections: AdminSection[] = [
  {
    label: "License Creation",
    id: "rv1-admin-licensing",
    route: "/reflector/admin-center/management/licensing",
    icon: "key",
  },
];

export const reflectorV2AdminSections: AdminSection[] = [
  {
    label: 'Licensing',
    id: 'rv2-licensing',
    route: '/reflector-v2/admin-center/management/licensing',
    icon: 'key',
  },
  {
    label: 'Releases',
    id: 'rv2-releases',
    route: '/reflector-v2/admin-center/releases',
    icon: 'tag',
  },
  {
    label: 'Log Collector Config',
    id: 'rv2-log-collector-config',
    route: '/reflector-v2/admin-center/log-collector-config',
  },
  {
    label: 'System Services',
    id: 'rv2-system-status',
    route: '/reflector-v2/admin-center/system-status',
    icon: 'terminal',
  },
];

/**
 * Filter admin sections based on user permissions
 * @param sections - The admin sections to filter
 * @param userPermissions - Array of permission strings the user has
 * @returns Filtered sections that the user has access to
 */
export function filterAdminSectionsByPermission(
  sections: AdminSection[],
  userPermissions: string[]
): AdminSection[] {
  return sections
    .map((section) => {
      // Check if section itself requires permission
      if (section.requiredPermission) {
        const required = Array.isArray(section.requiredPermission)
          ? section.requiredPermission
          : [section.requiredPermission];
        const hasPermission = required.some((p) => userPermissions.includes(p));
        if (!hasPermission) {
          return null; // User doesn't have permission for this section
        }
      }

      // If section has children, filter them recursively
      if (section.children) {
        const filteredChildren = filterAdminSectionsByPermission(
          section.children,
          userPermissions
        );
        // If no children remain after filtering, hide the section
        if (filteredChildren.length === 0) {
          return null;
        }
        return { ...section, children: filteredChildren };
      }

      return section;
    })
    .filter((section): section is AdminSection => section !== null);
}
  
  // { label: 'Data center', route: '/kontrol/admin-center/data-center' },
  // { label: 'Hardware', 
  //   id: 'hardware',
  //   children: [
  //     {
  //       label: "Port",
  //       route:"/kontrol/admin-center/hardware"
  //     },
  //   ]
  // },
  // { label: 'Hardware', route: '/kontrol/admin-center/hardware' },
  // { label: 'Port', route: '/kontrol/admin-center/port' },
  // { label: 'Internet', route: '/kontrol/admin-center/internet' },
  // { label: 'Cloud connectivity', route: '/kontrol/admin-center/cloud' },
  // { label: 'Third party Ports', route: '/kontrol/admin-center/third-party' },
  // { label: 'Fiber coverage', route: '/kontrol/admin-center/lastMile' },
  // { label: 'Data center connections', route: '/kontrol/admin-center/dc-connections' },
  // { label: 'IP block', route: '/kontrol/admin-center/ip-block' },
  // { label: 'Public subnet stock', route: '/kontrol/admin-center/public-subnet' },